import React from 'react';

/**
 * Premium clean world map visual.
 * Uses a real, geographically accurate world map SVG (Wikipedia Commons,
 * recoloured to cyan) hosted locally as /world-map.svg, with glowing region
 * nodes positioned over real geographic coordinates.
 */
export const WorldMap = ({ className = '' }) => {
  // Region markers positioned by approximate geographic location
  // (% of full equirectangular world-map width/height)
  const regions = [
    { id: 'na', label: 'North America', short: 'NA', left: '25%', top: '37%' },
    { id: 'uk', label: 'United Kingdom', short: 'UK', left: '47.2%', top: '27%' },
    { id: 'gcc', label: 'GCC', short: 'GCC', left: '60.5%', top: '42%' },
    { id: 'in', label: 'India', short: 'IN', left: '65.5%', top: '41%' },
  ];

  return (
    <div
      className={`relative w-full h-full ${className}`}
      role="img"
      aria-label="Flat world map showing Jixdo's regional coverage: India, GCC, United Kingdom and North America"
    >
      {/* Real geographic world map (recoloured to cyan) */}
      <div
        className="absolute inset-0 bg-no-repeat bg-center"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/world-map.svg)`,
          backgroundSize: 'contain',
        }}
      />

      {/* Connection arcs (SVG overlay) */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 56"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" stroke="#19B5FF" strokeOpacity="0.5" strokeWidth="0.18" strokeDasharray="0.7,0.8">
          {/* NA → UK */}
          <path d="M25,20.7 Q36,10 47.2,15.1" />
          {/* UK → GCC */}
          <path d="M47.2,15.1 Q54,18 60.5,23.5" />
          {/* GCC → India */}
          <path d="M60.5,23.5 Q63,23 65.5,23" />
        </g>
      </svg>

      {/* Region nodes */}
      {regions.map((r) => (
        <div
          key={r.id}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: r.left, top: r.top }}
        >
          {/* Outer glow */}
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 rounded-full"
            style={{
              width: '44px',
              height: '44px',
              background: 'radial-gradient(circle, rgba(25,181,255,0.45) 0%, rgba(25,181,255,0.12) 40%, transparent 70%)',
            }}
          />
          {/* Pulsing ring */}
          <div className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 w-3 h-3 rounded-full border border-[#19B5FF] animate-ping opacity-60" />
          {/* Core dot */}
          <div className="relative w-2.5 h-2.5 rounded-full bg-[#19B5FF] shadow-[0_0_12px_rgba(25,181,255,0.9)]">
            <div className="absolute inset-[2px] rounded-full bg-[#EAF4FF]" />
          </div>
          {/* Label */}
          <span className="absolute left-1/2 -translate-x-1/2 -top-5 text-[9px] uppercase tracking-[0.18em] text-[#EAF4FF] font-bold whitespace-nowrap drop-shadow-[0_1px_4px_rgba(6,18,30,0.9)]">
            {r.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export default WorldMap;
